import { useEffect, useState } from "react";
import { loadingHide, loadingShow } from "../../../loadingUi/loading.controller";
import { getApi } from "../../../api/call.api";
import { urlApi } from "../../../api/url.api";
import { IconCopy } from "@tabler/icons-react";
import { toast } from "sonner";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";

const ChatDocumentation = () => {
    const [docData, setDocData] = useState("");
    const [currentSiteURL, setCurrentSiteURL] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("cdn");

    const getData = async () => {
        loadingShow();
        let resp = await getApi(urlApi.getChatCDN);
        loadingHide();
        if (resp.responseCode === 200) {
            setDocData(resp.data);
        } else {
            setDocData(null);
        }
    };

    const getCurrentSite = async () => {
        setIsLoading(true);
        let resp = await getApi(urlApi.getChatDomain);
        setIsLoading(false);
        if (resp.responseCode === 200) {
            setCurrentSiteURL(resp?.data || null);
        } else {
            setCurrentSiteURL(null);
        }
    };

    useEffect(() => {
        getData();
        getCurrentSite();
        return () => {
            setDocData(null);
        };
    }, []);

    const onCopyScript = (script, label = "Script") => {
        if (script) {
            navigator.clipboard.writeText(script);
            toast.success(`${label} copied to clipboard!`);
        } else {
            toast.error("Nothing to copy.");
        }
    };

    const handleRemoveSite = async () => {
        loadingShow();
        let resp = await getApi(urlApi.removeChatIntegration);
        loadingHide();
        if (resp.responseCode === 200) {
            setCurrentSiteURL(null);
            toast.success(resp?.message);
        } else {
            toast.error(resp.message || "Failed to remove site integration.");
        }
    };

    const renderCDNContent = () => (
        <>
            <h1 className="text-3xl font-bold mb-4">📦 How to Set Up the CDN</h1>
            <p className="mb-6 text-lg">
                To use our service on your website, you can integrate it via CDN. Just follow the steps below:
            </p>
            <h2 className="text-2xl font-semibold mb-2">Step 1: Add Script Tag</h2>
            <p className="mb-4">
                Copy and paste the following script tag inside the <code>&lt;head&gt;</code> tag of your HTML file:
            </p>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto relative">
                <button
                    className="absolute right-0.5 top-0.5 shadow-md p-0.5 rounded-lg"
                    onClick={() => onCopyScript(`<script src="${docData}"></script>`, "CDN script")}
                    title="Copy"
                >
                    <IconCopy size={20} />
                </button>
                <code>{`<script src="${docData}"></script>`}</code>
            </pre>
        </>
    );

    const renderShopifyContent = () => (
        <>
            <h1 className="text-3xl font-bold mb-4">🛍️ Shopify Integration</h1>
            <p className="mb-4">
                Go to <strong>Online Store &gt; Themes &gt; Edit Code</strong>, then open the <code>theme.liquid</code> file.
            </p>
            <p className="mb-4">
                Paste the following code just before the <code>&lt;/head&gt;</code> tag:
            </p>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto relative">
                <button
                    className="absolute right-0.5 top-0.5 shadow-md p-0.5 rounded-lg"
                    onClick={() => onCopyScript(`<script src="${docData}"></script>`, "Shopify script")}
                    title="Copy"
                >
                    <IconCopy size={20} />
                </button>
                <code>{`<script src="${docData}"></script>`}</code>
            </pre>
        </>
    );

    const renderWordPressContent = () => (
        <>
            <h1 className="text-3xl font-bold mb-4">📝 WordPress Integration</h1>
            <p className="mb-4">
                Install the <strong>Insert Headers and Footers</strong> plugin, and paste the following script inside the <strong>Header</strong> section:
            </p>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto relative">
                <button
                    className="absolute right-0.5 top-0.5 shadow-md p-0.5 rounded-lg"
                    onClick={() => onCopyScript(`<script src="${docData}"></script>`, "WordPress script")}
                    title="Copy"
                >
                    <IconCopy size={20} />
                </button>
                <code>{`<script src="${docData}"></script>`}</code>
            </pre>
        </>
    );

    const renderWooCommerceContent = () => (
        <>
            <h1 className="text-3xl font-bold mb-4">🛒 WooCommerce Integration</h1>
            <p className="mb-4">
                Since WooCommerce runs on WordPress, use the same steps. Add the script via header plugin or directly into <code>header.php</code>.
            </p>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto relative">
                <button
                    className="absolute right-0.5 top-0.5 shadow-md p-0.5 rounded-lg"
                    onClick={() => onCopyScript(`<script src="${docData}"></script>`, "WooCommerce script")}
                    title="Copy"
                >
                    <IconCopy size={20} />
                </button>
                <code>{`<script src="${docData}"></script>`}</code>
            </pre>
        </>
    );

    const renderAppSDKContent = () => (
        <>
            <h1 className="text-3xl font-bold mb-4">📱 App SDK Integration (Flutter)</h1>

            <h2 className="text-2xl font-semibold mb-2">Installation</h2>
            <p className="mb-4">Add to your <code>pubspec.yaml</code>:</p>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto">
                {`dependencies:
  advy_chat: ^1.1.1`}
            </pre>

            <h2 className="text-2xl font-semibold mb-2">Run</h2>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto">
                flutter pub get
            </pre>

            <h2 className="text-2xl font-semibold mb-2">Usage Example</h2>
            <pre className="bg-gray-100 text-sm p-4 rounded border border-gray-300 mb-6 overflow-x-auto">
                {`import 'package:flutter/material.dart';
import 'package:advy_chat/advy_chat.dart';

void main() {
  runApp(const MyApp());
}

class MyApp extends StatelessWidget {
  const MyApp({super.key});

  @override
  Widget build(BuildContext context) {
    return MaterialApp(
      title: 'Attriva Chat Demo',
      home: Scaffold(
        appBar: AppBar(title: const Text('Attriva Chat Example')),
        body: Center(
          child: AdvyWidget(
            userName: 'John Doe',
            mobileNumber: '9876543210',
            productKey: 'YOUR_PRODUCT_KEY_HERE',
          ),
        ),
      ),
    );
  }
}`}
            </pre>
        </>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case "cdn": return renderCDNContent();
            case "shopify": return renderShopifyContent();
            case "wordpress": return renderWordPressContent();
            case "woocommerce": return renderWooCommerceContent();
            case "appsdk": return renderAppSDKContent();
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-white p-6 md:p-12 text-gray-800">
            <div className="max-w-4xl mx-auto">

                {/* Site status box */}
                <div className="mb-6">
                    {isLoading ? (
                        <div className="animate-pulse flex items-center justify-between bg-gray-100 border rounded p-4">
                            <div className="h-4 bg-gray-300 rounded w-2/3" />
                            <div className="h-8 bg-gray-300 rounded w-24" />
                        </div>
                    ) : currentSiteURL ? (
                        <div className="flex items-center justify-between bg-green-50 border border-green-300 rounded p-4">
                            <div>
                                <p className="text-sm text-gray-600">Currently integrated with:</p>
                                <p className="text-lg font-medium text-green-700">{currentSiteURL}</p>
                            </div>
                            <ConfirmationModal
                                buttonComponent={
                                    <button className="bg-red-100 text-red-600 hover:bg-red-200 text-sm font-semibold px-4 py-2 rounded shadow-sm border border-red-300 transition-all">
                                        Remove Site
                                    </button>
                                }
                                confirmationText="Are you sure you want to remove CDN Integration from the site?"
                                onConfirm={handleRemoveSite}
                            />
                        </div>
                    ) : (
                        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm p-4 rounded">
                            No site currently integrated.
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex gap-3 mb-6 border-b border-gray-200">
                    {[
                        { id: "cdn", label: "CDN" },
                        { id: "shopify", label: "Shopify" },
                        { id: "wordpress", label: "WordPress" },
                        { id: "woocommerce", label: "WooCommerce" },
                        { id: "appsdk", label: "App SDK" }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`capitalize py-2 px-4 text-sm font-medium border-b-2 ${activeTab === tab.id
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-blue-500"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {renderTabContent()}
            </div>
        </div>
    );
};

export default ChatDocumentation;
